import { apiRequest } from '../../shared/api/client';
import { subscribeToMatch, emitWithAck, MatchUpdateEvent } from '../../shared/api/socket';
import { computeStateDelta } from './stateDiff';
import { MatchStatus, VersionChannel } from '../../shared/types/Match';

export type CallbackFn = (newState: unknown) => void;

export type MatchPlayer = {
  id: string;
  name: string;
  imageUrl?: string | null;
};

// Re-export for convenience
export type { MatchStatus, VersionChannel };

export type ExecutionMode = 'client' | 'server';

export type MatchDocument = {
  id: string;
  game: string;
  name: string;
  gameName: string;
  status: MatchStatus;
  majorVersion: number;
  minorVersionChannel?: VersionChannel;
  executionMode: ExecutionMode;
  players: MatchPlayer[];
  options?: Record<string, string>;
  state: unknown;
  stateVersion?: string; // For optimistic locking (session-unique ID)
  // Store raw server player data for diffing
  serverPlayers?: Record<string, {
    odId: string;
    name: string;
    imageUrl?: string | null;
    prompt?: string;
    promptTime?: number;
    score?: number;
  }>;
};

export type MatchStorage = {
  dispose: () => void;
  readGameFastData: () => Promise<{
    game: string;
    name: string;
    majorVersion: number;
    minorVersionChannel?: VersionChannel;
  }>;
  readDocument: () => Promise<MatchDocument>;
  readState: () => Promise<unknown>;
  saveState: (
    newState: unknown,
    players: any[],
    matchState: "active" | "finished",
    winners: any[]
  ) => Promise<void>;
  registerNewStateCallback: (cb: CallbackFn) => () => void;
};

// Server response format for a match
interface MatchResponse {
  id: string;
  name: string;
  gameName: string;
  gameId: string;
  status: MatchStatus;
  majorVersion: number;
  minorVersionChannel?: string;
  executionMode: ExecutionMode;
  playerUserIds: string[];
  players: Record<string, {
    odId: string;
    name: string;
    imageUrl?: string | null;
    prompt?: string;
    promptTime?: number;
    score?: number;
  }>;
  options?: Record<string, string>;
  state?: unknown;
  stateVersion?: string;
  winners?: string[];
  createdAt: string;
  lastUpdated?: string;
}

function processMatchResponse(match: MatchResponse): MatchDocument {
  return {
    id: match.id,
    game: match.gameId,
    name: match.name,
    gameName: match.gameName,
    status: match.status,
    majorVersion: match.majorVersion,
    minorVersionChannel: match.minorVersionChannel as VersionChannel | undefined,
    executionMode: match.executionMode || 'client',
    options: match.options,
    players: Object.values(match.players)
      .sort((a, b) => a.odId.localeCompare(b.odId))
      .map((p) => ({
        id: p.odId,
        name: p.name,
        imageUrl: p.imageUrl,
      })),
    state: match.state ?? {},
    stateVersion: match.stateVersion ?? '',
    serverPlayers: match.players, // Store raw server player data for diffing
  };
}

/**
 * Creates a match storage instance that manages WebSocket synchronization
 * for a game match.
 *
 * @param matchId - The match ID
 */
export function createMatchStorage(
  matchId: string,
): MatchStorage {
  let callbacks: CallbackFn[] = [];
  let callbacksFastState: CallbackFn[] = [];
  let matchContents: MatchDocument | undefined = undefined;
  let unsubscribeFromMatch: (() => void) | null = null;
  let isConnected = false;

  // Subscribe to match updates via shared socket
  const setupSubscription = async () => {
    try {
      unsubscribeFromMatch = await subscribeToMatch(
        matchId,
        (data: MatchUpdateEvent) => {
          console.log('Got match update via socket');
          const newMatchContents = processMatchResponse(data.match as unknown as MatchResponse);

          // Check if state actually changed
          const stateChanged = JSON.stringify(matchContents?.state) !== JSON.stringify(newMatchContents.state);

          matchContents = newMatchContents;

          if (stateChanged) {
            // Deep clone state so games can safely mutate their copy
            // without corrupting our baseline for delta computation
            callbacks.forEach((cb) => cb(structuredClone(matchContents!.state)));
          }
        },
        () => {
          console.log('Match was deleted');
          // Could trigger navigation or show error
        }
      );
      isConnected = true;
      console.log('Subscribed to match updates:', matchId);
    } catch (err) {
      console.error('Failed to subscribe to match updates:', err);
      isConnected = false;
    }
  };

  // Initial fetch to get current match state
  const fetchMatch = async (): Promise<MatchResponse> => {
    const response = await apiRequest<{ match: MatchResponse }>(`/api/matches/${matchId}`);
    return response.match;
  };

  // Initialize subscription
  setupSubscription();

  // Fetch initial state
  fetchMatch()
    .then((match) => {
      matchContents = processMatchResponse(match);
      // Notify fast state callbacks with raw data for quick loading
      callbacksFastState.forEach((cb) => cb({
        game: match.gameId,
        name: match.name,
        majorVersion: match.majorVersion,
        minorVersionChannel: match.minorVersionChannel as VersionChannel | undefined,
      }));
      // Notify state callbacks (deep clone so games can safely mutate)
      callbacks.forEach((cb) => cb(structuredClone(matchContents!.state)));
    })
    .catch((err) => {
      console.error('Failed to fetch initial match data:', err);
    });

  const dispose = () => {
    if (unsubscribeFromMatch) {
      unsubscribeFromMatch();
      unsubscribeFromMatch = null;
    }
    callbacks = [];
    callbacksFastState = [];
    isConnected = false;
  };

  const registerNewStateCallback = (cb: CallbackFn) => {
    callbacks.push(cb);
    // If we already have match contents, call the callback immediately
    // Deep clone so games can safely mutate their copy
    if (matchContents) {
      cb(structuredClone(matchContents.state));
    }
    return () => {
      callbacks = callbacks.filter((c) => c !== cb);
    };
  };

  const registerNewFastStateCallback = (cb: CallbackFn) => {
    callbacksFastState.push(cb);
    // If we already have match contents, call the callback immediately
    if (matchContents) {
      cb({
        game: matchContents.game,
        name: matchContents.name,
        majorVersion: matchContents.majorVersion,
        minorVersionChannel: matchContents.minorVersionChannel,
      });
    }
    return () => {
      callbacksFastState = callbacksFastState.filter((c) => c !== cb);
    };
  };

  const saveState = async (
    newState: unknown,
    players: any[],
    matchState: "active" | "finished",
    winners: any[]
  ) => {
    if (!isConnected) {
      console.error('Socket not connected, cannot save state');
      return;
    }

    if (!matchContents) {
      console.error('No match contents available');
      return;
    }

    // Serialize to plain objects: calls toJSON(), strips circular refs and non-serializable values.
    const cleanState = JSON.parse(JSON.stringify(newState));

    // Compute state delta (only changed paths) with previous values for field-level CAS
    const { delta: stateDelta, previousValues } = computeStateDelta(matchContents.state, cleanState);
    const hasStateChanges = Object.keys(stateDelta).length > 0;

    // Update state baseline immediately so the next saveState call computes
    // its delta against what we just sent, not the stale baseline.
    if (hasStateChanges) {
      matchContents.state = cleanState;
    }

    // Build player updates - only include changed fields
    const playerUpdates: Record<string, Record<string, unknown>> = {};
    let hasPlayerUpdates = false;
    const currentServerPlayers = matchContents.serverPlayers;

    players.forEach((player) => {
      const updates: Record<string, unknown> = {};

      // Get current values
      const promptMessage = player.promptStatus?.latestPromptMessage ?? "";
      const promptTime = player.promptStatus?.latestPromptResponseTime ?? 0;
      const score = player.matchScoreNumber;

      // Get previous values from server player data
      const previousPlayerData = currentServerPlayers?.[player.id];
      const previousPrompt = previousPlayerData?.prompt ?? "";
      const previousPromptTime = previousPlayerData?.promptTime ?? 0;
      const previousScore = previousPlayerData?.score;

      // Only send prompt if it changed
      if (promptMessage !== previousPrompt) {
        updates.prompt = promptMessage;
        // Only set timestamp when there's a new prompt (not when clearing)
        if (promptMessage.length > 0) {
          updates.promptTimestamp = new Date().toISOString();
        }
      }

      // Only send promptTime if it changed
      if (promptTime !== previousPromptTime) {
        updates.promptTime = promptTime;
      }

      // Only send score if it changed
      if (score !== undefined && score !== previousScore) {
        updates.score = score;
      }

      if (Object.keys(updates).length > 0) {
        playerUpdates[player.id] = updates;
        hasPlayerUpdates = true;
      }
    });

    // Check if we have anything to update
    const hasStatusChange = matchState !== matchContents.status;
    const hasWinnersChange = matchState === 'finished' && winners && winners.length > 0;
    const hasAnyUpdates = hasStateChanges || hasPlayerUpdates || hasStatusChange || hasWinnersChange;

    if (!hasAnyUpdates) {
      console.log('No changes to save, skipping update');
      return;
    }

    // Send targeted state update via WebSocket with previousValues for field-level CAS
    emitWithAck<{ success: boolean; error?: string; newVersion?: string }>(
      'match:updateState',
      {
        matchId,
        stateDelta: hasStateChanges ? stateDelta : undefined,
        previousValues: hasStateChanges ? previousValues : undefined,
        stateVersion: matchContents.stateVersion || undefined,
        status: hasStatusChange ? matchState : undefined,
        players: hasPlayerUpdates ? playerUpdates : undefined,
        winners: hasWinnersChange ? winners.map((w) => w.id) : undefined,
      },
      (response) => {
        if (response.success) {
          console.log('State update saved successfully');
          if (matchContents && response.newVersion) {
            matchContents.stateVersion = response.newVersion;
          }
        } else {
          console.error('Failed to save state:', response.error);
          if (response.error?.includes('conflict')) {
            console.warn('Field-level conflict detected - state may be out of sync');
          }
        }
      }
    );
  };

  const readDocument = async (): Promise<MatchDocument> => {
    if (matchContents) {
      return matchContents;
    }

    // Wait for initial fetch to complete
    return new Promise((resolve) => {
      const done = registerNewStateCallback(() => {
        if (matchContents) {
          resolve(matchContents);
          done();
        }
      });
    });
  };

  // Fast data that can come from initial fetch
  const readGameFastData = async () => {
    if (matchContents) {
      return {
        game: matchContents.game,
        name: matchContents.name,
        majorVersion: matchContents.majorVersion,
        minorVersionChannel: matchContents.minorVersionChannel,
      };
    }

    return new Promise<{ game: string; name: string; majorVersion: number; minorVersionChannel?: VersionChannel }>(
      (resolve) => {
        const done = registerNewFastStateCallback((data: any) => {
          resolve({
            game: data.game,
            name: data.name,
            majorVersion: data.majorVersion,
            minorVersionChannel: data.minorVersionChannel,
          });
          done();
        });
      }
    );
  };

  const readState = async () => {
    return (await readDocument()).state;
  };

  return {
    dispose,
    readGameFastData,
    readDocument,
    readState,
    saveState,
    registerNewStateCallback,
  };
}
